import { getBaselineDefinition } from "../baselines/registry";
import type { BaselineRecord } from "../baselines/types";
import { sumTotalEncounterVolumes } from "./encounter-volumes";
import {
  mergeOrgProfileFacts,
  type OrgProfileFact,
  type OrgProfileFactSourceKind,
  serializeOrgProfileFactScope,
  sourceKindFromBaselineSource,
} from "./facts";
import type { ProfileData } from "./types";

type OrgProfileLike = {
  name?: unknown;
  orgType?: unknown;
  annualRevenue?: unknown;
  bedCount?: unknown;
  physicianCount?: unknown;
  region?: unknown;
  state?: unknown;
  settingType?: unknown;
  teachingStatus?: unknown;
  ehrSystem?: unknown;
  annualAiBudget?: unknown;
  profileData?: unknown;
  baselineRecords?: unknown;
  tier1Inputs?: unknown;
};

type FactDraftInput = Omit<OrgProfileFact, "scopeKey"> & {
  scopeKey?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function presentString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function addFact(facts: FactDraftInput[], fact: FactDraftInput) {
  if (fact.value === undefined || fact.value === null) return;
  facts.push({
    ...fact,
    scopeKey: fact.scopeKey ?? serializeOrgProfileFactScope(fact.scope),
  });
}

function numberEntries(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, number] =>
        typeof entry[1] === "number" && Number.isFinite(entry[1]),
    ),
  );
}

function getTier1Inputs(profile: OrgProfileLike): Record<string, unknown> {
  return isRecord(profile.tier1Inputs) ? profile.tier1Inputs : {};
}

function hasAny(set: Set<string>): boolean {
  return set.size > 0;
}

function profileData(profile: OrgProfileLike): Partial<ProfileData> {
  return isRecord(profile.profileData)
    ? (profile.profileData as Partial<ProfileData>)
    : {};
}

function derivedSource(input: {
  hasReportedDependency: boolean;
  defaultLabel: string;
  derivedLabel: string;
}): { sourceKind: OrgProfileFactSourceKind; sourceLabel: string } {
  return input.hasReportedDependency
    ? {
        sourceKind: "derived_from_user",
        sourceLabel: input.derivedLabel,
      }
    : {
        sourceKind: "benchmark_default",
        sourceLabel: input.defaultLabel,
      };
}

function addTopLevelFacts(profile: OrgProfileLike, facts: FactDraftInput[]) {
  const textFacts: Array<[string, unknown, string]> = [
    ["name", profile.name, "Organization profile name"],
    ["org_type", profile.orgType, "Organization type"],
    ["region", profile.region, "Organization region"],
    ["state", profile.state, "Organization state"],
    ["setting_type", profile.settingType, "Organization setting type"],
    ["ehr_system", profile.ehrSystem, "EHR system"],
  ];
  for (const [fieldPath, value, sourceLabel] of textFacts) {
    const text = presentString(value);
    if (!text) continue;
    addFact(facts, {
      fieldPath,
      value: text,
      sourceKind: "user_provided",
      sourceLabel,
      confidence: 0.95,
    });
  }

  const numberFacts: Array<[string, unknown, string, string | undefined]> = [
    ["annual_revenue", profile.annualRevenue, "Annual revenue", "currency"],
    ["bed_count", profile.bedCount, "Licensed bed count", "count"],
    ["physician_count", profile.physicianCount, "Physician count", "count"],
    [
      "annual_ai_budget",
      profile.annualAiBudget,
      "Annual AI budget",
      "currency",
    ],
  ];
  for (const [fieldPath, value, sourceLabel, unit] of numberFacts) {
    const numeric = finiteNumber(value);
    if (numeric == null) continue;
    addFact(facts, {
      fieldPath,
      value: numeric,
      sourceKind: "user_provided",
      sourceLabel,
      unit,
      confidence: 0.95,
    });
  }

  if (typeof profile.teachingStatus === "boolean") {
    addFact(facts, {
      fieldPath: "teaching_status",
      value: profile.teachingStatus,
      sourceKind: "user_provided",
      sourceLabel: "Teaching status",
      confidence: 0.95,
    });
  }
}

function addTotalEncounterFact(
  profile: OrgProfileLike,
  facts: FactDraftInput[],
) {
  const tier1 = getTier1Inputs(profile);
  const reportedTotal = finiteNumber(tier1.totalEncounters);
  if (reportedTotal != null) {
    addFact(facts, {
      fieldPath: "total_encounters",
      value: reportedTotal,
      sourceKind: "user_provided",
      sourceLabel: "Org profile total encounter volume",
      unit: "encounters",
      confidence: 0.95,
    });
    return;
  }

  const encounters = numberEntries(profileData(profile).encounter_volumes);
  const total = sumTotalEncounterVolumes(encounters);
  if (total == null || total <= 0) return;
  const encounterOverrides = numberEntries(tier1.encounterOverrides);
  addFact(facts, {
    fieldPath: "total_encounters",
    value: total,
    ...derivedSource({
      hasReportedDependency: Object.keys(encounterOverrides).length > 0,
      derivedLabel: "Summed from reported org profile encounter volumes",
      defaultLabel: "Summed from estimated org profile encounter volumes",
    }),
    unit: "encounters",
    confidence: Object.keys(encounterOverrides).length > 0 ? 0.84 : 0.7,
  });
}

function addProfileDataFacts(profile: OrgProfileLike, facts: FactDraftInput[]) {
  const tier1 = getTier1Inputs(profile);
  const data = profileData(profile);
  const encounterVolumes = numberEntries(data.encounter_volumes);
  const reportedEncounterVolumes = numberEntries(tier1.encounterOverrides);
  const reportedEncounterKeys = new Set(Object.keys(reportedEncounterVolumes));
  const reportedTotal = finiteNumber(tier1.totalEncounters);

  for (const [key, value] of Object.entries(encounterVolumes)) {
    const reported = reportedEncounterKeys.has(key);
    addFact(facts, {
      fieldPath: `encounter_volumes.${key}`,
      value: reported ? (reportedEncounterVolumes[key] ?? value) : value,
      sourceKind: reported
        ? "user_provided"
        : reportedTotal != null || hasAny(reportedEncounterKeys)
          ? "derived_from_user"
          : "benchmark_default",
      sourceLabel: reported
        ? `Org profile ${key} encounter volume`
        : reportedTotal != null || hasAny(reportedEncounterKeys)
          ? "Estimated distribution from reported encounter inputs"
          : "Estimated from org type encounter distribution defaults",
      unit: "encounters",
      confidence: reported ? 0.95 : 0.72,
    });
  }

  const payerMix = numberEntries(data.payer_mix);
  const reportedPayerMix = numberEntries(tier1.payerMix);
  const reportedPayerKeys = new Set(Object.keys(reportedPayerMix));
  for (const [key, value] of Object.entries(payerMix)) {
    const reported = reportedPayerKeys.has(key);
    addFact(facts, {
      fieldPath: `payer_mix.${key}`,
      value,
      sourceKind: reported
        ? "user_provided"
        : hasAny(reportedPayerKeys)
          ? "derived_from_user"
          : "benchmark_default",
      sourceLabel: reported
        ? `Org profile payer mix: ${key}`
        : hasAny(reportedPayerKeys)
          ? "Balanced from reported payer-mix inputs"
          : "Estimated from org type and state payer-mix defaults",
      unit: "share",
      confidence: reported ? 0.95 : 0.68,
    });
  }

  const specialtyMix = numberEntries(data.specialty_mix);
  const reportedSpecialtyMix = numberEntries(tier1.specialtyMixOverrides);
  const reportedSpecialtyKeys = new Set(Object.keys(reportedSpecialtyMix));
  const selectedSpecialties = Array.isArray(tier1.selectedSpecialties)
    ? tier1.selectedSpecialties
    : [];
  for (const [key, value] of Object.entries(specialtyMix)) {
    const reported = reportedSpecialtyKeys.has(key);
    addFact(facts, {
      fieldPath: `specialty_mix.${key}`,
      value,
      sourceKind: reported
        ? "user_provided"
        : selectedSpecialties.length > 0
          ? "derived_from_user"
          : "benchmark_default",
      sourceLabel: reported
        ? `Org profile specialty mix: ${key}`
        : selectedSpecialties.length > 0
          ? "Estimated across selected specialties"
          : "Estimated from org type specialty defaults",
      unit: "share",
      confidence: reported ? 0.95 : 0.68,
    });
  }

  if (selectedSpecialties.length > 0) {
    addFact(facts, {
      fieldPath: "selected_specialties",
      value: selectedSpecialties,
      sourceKind: "user_provided",
      sourceLabel: "Selected specialties",
      confidence: 0.95,
    });
  }

  const workforce = isRecord(data.workforce) ? data.workforce : {};
  const reportedWorkforce = isRecord(tier1.workforceOverrides)
    ? tier1.workforceOverrides
    : {};
  for (const [role, rawEntry] of Object.entries(workforce)) {
    if (!isRecord(rawEntry)) continue;
    const entry = rawEntry as Record<string, unknown>;
    const overrides = isRecord(reportedWorkforce[role])
      ? (reportedWorkforce[role] as Record<string, unknown>)
      : {};
    const ftes = finiteNumber(entry.ftes);
    if (ftes != null) {
      const reportedFtes = finiteNumber(overrides.ftes);
      const reported = reportedFtes != null;
      addFact(facts, {
        fieldPath: `workforce.${role}.ftes`,
        value: reported ? reportedFtes : ftes,
        sourceKind: reported ? "user_provided" : "derived_from_user",
        sourceLabel: reported
          ? `Org profile workforce FTEs: ${role}`
          : "Estimated from org profile scale inputs",
        unit: "fte",
        confidence: reported ? 0.95 : 0.72,
      });
    }
    const avgCost = finiteNumber(entry.avg_fully_loaded_cost);
    if (avgCost != null) {
      const reportedAvgCost = finiteNumber(overrides.avgCost);
      const reported = reportedAvgCost != null;
      addFact(facts, {
        fieldPath: `workforce.${role}.avg_fully_loaded_cost`,
        value: reported ? reportedAvgCost : avgCost,
        sourceKind: reported ? "user_provided" : "benchmark_default",
        sourceLabel: reported
          ? `Org profile workforce cost: ${role}`
          : "Estimated from workforce cost benchmark",
        unit: "currency",
        confidence: reported ? 0.95 : 0.64,
      });
    }
  }
}

function addBaselineFacts(profile: OrgProfileLike, facts: FactDraftInput[]) {
  const records = Array.isArray(profile.baselineRecords)
    ? (profile.baselineRecords as BaselineRecord[])
    : [];
  for (const record of records) {
    if (!record || typeof record.key !== "string") continue;
    if (record.value === undefined) continue;
    const definition = getBaselineDefinition(record.key);
    addFact(facts, {
      fieldPath: `baseline_records.${record.key}`,
      value: record.value,
      scope: record.scope,
      sourceKind: sourceKindFromBaselineSource(record.source),
      sourceLabel: record.sourceLabel,
      unit: definition?.unit,
      confidence:
        record.source === "confirmed"
          ? 0.98
          : record.source === "customer_reported"
            ? 0.92
            : record.source === "estimated"
              ? 0.72
              : 0.62,
      providedBy: record.providedBy,
      updatedBy: record.updatedBy,
    });
  }
}

export function buildOrgProfileFactsFromProfile(
  profile: OrgProfileLike,
): OrgProfileFact[] {
  const facts: FactDraftInput[] = [];
  addTopLevelFacts(profile, facts);
  addTotalEncounterFact(profile, facts);
  addProfileDataFacts(profile, facts);
  addBaselineFacts(profile, facts);
  return mergeOrgProfileFacts(facts);
}
